// FILE: src/components/app/bases/TransactionsBaseComponent/MultiEditTransactionsModal/MultiEditTransactionsModal.js
import React, { useEffect, useMemo, useState } from "react";
import styles from "./MultiEditTransactionsModal.module.css";

import { TagsAPI, SystemAPI, TransactionsAPI } from "../../../../../utils/firebase";
import { normalizeFullPath } from "../../UploadDataComponent/utils/tagUtils";
import { parseAmountValue } from "../TransactionsTableComponent/TransactionsTableComponent";

/**
 * Bulk Edit modal
 *
 * Props:
 * - user
 * - open
 * - rows: array of selected transaction objects (must include id)
 * - onClose()
 * - onSaved(updatedRows[]) — called after successful update
 * - descriptionHints?: string[] (optional, used for description typeahead)
 * - accounts: array of { id, name, currencyCode, ... }  ← from base
 */
export default function MultiEditTransactionsModal({
  user,
  open,
  rows = [],
  onClose,
  onSaved,
  descriptionHints = [],
  accounts: accountsProp = [],
}) {
  const count = rows?.length || 0;
  const userEmail = String(user?.email || "").trim();

  // form state (empty means "no changes")
  const [typeName, setTypeName] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [accountId, setAccountId] = useState("");
  const [date, setDate] = useState("");
  const [memo, setMemo] = useState("");
  const [status, setStatus] = useState("");
  const [url, setUrl] = useState("");
  const [tagsAdd, setTagsAdd] = useState("");
  const [tagsRemove, setTagsRemove] = useState("");

  // accounts from parent
  const [accounts, setAccounts] = useState([]);
  useEffect(() => { setAccounts(Array.isArray(accountsProp) ? accountsProp : []); }, [accountsProp]);

  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [txnTypes, setTxnTypes] = useState([]);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // reset form when opening
  useEffect(() => {
    if (!open) return;
    setTypeName("");
    setDescription("");
    setAmount("");
    setAccountId("");
    setDate("");
    setMemo("");
    setStatus("");
    setUrl("");
    setTagsAdd("");
    setTagsRemove("");
    setErr(null);
    setBusy(false);
  }, [open]);

  // load system transaction types
  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      try {
        const typesList = await SystemAPI.fetchTransactionTypes();
        if (!alive) return;
        const types = (typesList || []).map((t) => ({
          id: t.id || t.code || t.name || String(t),
          name: t.name || t.label || t.code || t.id || String(t),
        }));
        setTxnTypes(types);
      } catch {
        if (!alive) return;
        setTxnTypes([]);
      }
    })();
    return () => { alive = false; };
  }, [open]);

  // subscribe to tags for typeahead
  useEffect(() => {
    if (!open || !userEmail) return;
    const unsub = TagsAPI.subscribeUserTags({
      userEmail,
      cap: 1000,
      onData: (docs) => {
        try {
          const fulls = [];
          for (const t of docs || []) {
            const p = normalizeFullPath(t.fullPath || t.name || "");
            if (p) fulls.push(p);
          }
          const unique = Array.from(new Set(fulls)).sort((a, b) => a.localeCompare(b));
          setTagSuggestions(unique);
        } catch { setTagSuggestions([]); }
      },
      onError: () => setTagSuggestions([]),
    });
    return () => { try { unsub && unsub(); } catch {} };
  }, [open, userEmail]);

  // derived
  const accountById = useMemo(() => {
    const m = new Map();
    for (const a of accounts) m.set(a.id, a);
    return m;
  }, [accounts]);

  const canSubmit = useMemo(() => {
    return Boolean(
      typeName || description || amount || accountId || date ||
      memo || status || url || tagsAdd || tagsRemove
    );
  }, [typeName, description, amount, accountId, date, memo, status, url, tagsAdd, tagsRemove]);

  // small helper: split comma list -> normalized tag paths
  const parseTags = (input) =>
    String(input || "")
      .split(",")
      .map((s) => normalizeFullPath(s.trim()))
      .filter(Boolean);

  // apply form patch to a single row
  const buildPatched = (row) => {
    const next = { ...row };

    if (typeName) next.typeName = typeName;
    if (description) next.description = description;

    if (amount) {
      const n = parseAmountValue(amount);
      if (Number.isFinite(n)) next.amount = n;
    }

    if (accountId) {
      const acct = accountById.get(accountId);
      next.accountId = accountId;
      next.accountName = acct?.name || next.accountName || "";
      if (acct?.currencyCode) next.currencyCode = acct.currencyCode;
    }

    if (date) next.date = date;
    if (memo) next.memo = memo;
    if (status) next.status = status;
    if (url) next.url = url;

    const adds = parseTags(tagsAdd);
    const removes = parseTags(tagsRemove);
    if (adds.length || removes.length) {
      const current = Array.isArray(next.tagPath) ? next.tagPath.slice() : [];
      const delSet = new Set(removes.map((r) => r.toLowerCase()));
      let out = current.filter((p) => !delSet.has(String(p).toLowerCase()));
      for (const a of adds) {
        if (!out.some((p) => String(p).toLowerCase() === a.toLowerCase())) out.push(a);
      }
      next.tagPath = out;
    }

    if (!next.source) next.source = "manual";
    return next;
  };

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit || !rows?.length) return;

    setBusy(true); setErr(null);
    try {
      const updated = [];
      for (const r of rows) {
        const tx = buildPatched(r);
        let saved = null;
        if (typeof TransactionsAPI.updateTransactionsPartial === "function") {
          const { id, ...patch } = tx;
          saved = await TransactionsAPI.updateTransactionsPartial({ user, id: r.id, patch });
          saved = { ...r, ...(saved || patch), id: r.id };
        } else if (typeof TransactionsAPI.updateManualTransaction === "function") {
          saved = await TransactionsAPI.updateManualTransaction({ user, tx });
        } else {
          saved = tx;
        }
        updated.push(saved || tx);
      }
      onSaved?.(updated);
      onClose?.();
    } catch (e2) {
      setErr(e2);
    } finally {
      setBusy(false);
    }
  };

  // description suggestions (limit 3; simple contains ranking)
  const descDatalist = useMemo(() => {
    const q = description.trim().toLowerCase();
    if (!q) return (descriptionHints || []).slice(0, 3);
    return (descriptionHints || [])
      .filter((s) => String(s).toLowerCase().includes(q))
      .slice(0, 3);
  }, [description, descriptionHints]);

  // tag suggestions (limit 5)
  const tagSuggestFiltered = (value) => {
    const parts = String(value || "").split(",");
    const q = normalizeFullPath(parts[parts.length - 1] || "");
    if (!q) return tagSuggestions.slice(0, 5);
    const lower = q.toLowerCase();
    return tagSuggestions.filter((t) => t.toLowerCase().includes(lower)).slice(0, 5);
  };

  const tagAddSuggestions = tagSuggestFiltered(tagsAdd);
  const tagRemoveSuggestions = tagSuggestFiltered(tagsRemove);

  return open ? (
    <div className={`modal d-block ${styles.modalBackdrop}`} role="dialog" aria-modal="true">
      <div className={`modal-dialog modal-lg modal-dialog-centered ${styles.dialog}`}>
        <div className="modal-content">
          <div className={`modal-header ${styles.header}`}>
            <h5 className="modal-title">Edit Transactions</h5>
            <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
          </div>

          <form onSubmit={onSubmit}>
            <div className={`modal-body ${styles.body}`}>
              <div className={`mb-3 ${styles.countRow}`}>
                <span className="text-secondary">{count} transaction{count !== 1 ? "s" : ""} selected</span>
              </div>

              {err && <div className="alert alert-danger py-2">{String(err?.message || err)}</div>}

              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <label className="form-label">Type</label>
                  <select className="form-select" value={typeName} onChange={(e) => setTypeName(e.target.value)}>
                    <option value="">{`-- no changes --`}</option>
                    {txnTypes.map((t) => (
                      <option key={t.id} value={t.name}>{t.name}</option>
                    ))}
                  </select>
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label">Description</label>
                  <input
                    className="form-control"
                    placeholder="-- no changes --"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    list="pp-desc-hints"
                  />
                  <datalist id="pp-desc-hints">
                    {descDatalist.map((s) => <option key={s} value={s} />)}
                  </datalist>
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label">Amount</label>
                  <input
                    className="form-control"
                    inputMode="decimal"
                    placeholder="-- no changes --"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label">Account</label>
                  <select className="form-select" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
                    <option value="">{`-- no changes --`}</option>
                    {accounts.map((a) => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label">Date</label>
                  <input
                    type="date"
                    className="form-control"
                    placeholder="-- no changes --"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label">Status</label>
                  <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="">{`-- no changes --`}</option>
                    <option value="CLEARED">CLEARED</option>
                    <option value="PENDING">PENDING</option>
                  </select>
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label">Tags to Add</label>
                  <input
                    className="form-control"
                    placeholder="-- no changes --"
                    value={tagsAdd}
                    onChange={(e) => setTagsAdd(e.target.value)}
                    list="pp-tags-add"
                  />
                  <datalist id="pp-tags-add">
                    {tagAddSuggestions.map((t) => <option key={t} value={t} />)}
                  </datalist>
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label">Tags to Remove</label>
                  <input
                    className="form-control"
                    placeholder="-- no changes --"
                    value={tagsRemove}
                    onChange={(e) => setTagsRemove(e.target.value)}
                    list="pp-tags-remove"
                  />
                  <datalist id="pp-tags-remove">
                    {tagRemoveSuggestions.map((t) => <option key={t} value={t} />)}
                  </datalist>
                </div>

                <div className="col-12">
                  <label className="form-label">Memo</label>
                  <textarea
                    rows={2}
                    className="form-control"
                    placeholder="-- no changes --"
                    value={memo}
                    onChange={(e) => setMemo(e.target.value)}
                  />
                </div>

                <div className="col-12">
                  <label className="form-label">URL</label>
                  <input
                    className="form-control"
                    placeholder="-- no changes --"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={busy}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={!canSubmit || busy}>
                {busy ? "Updating…" : "Update Selected Transactions"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  ) : null;
}
